import { createContext } from 'react';
import { TChannel } from '../operator/operator-context';
import { Item, Offer, Timeslot, TSite } from '../site/site-context';
import basketItem from "../../components/pages/Menu/components/basket/BasketItem";
import type { Time } from "../../types/time";

export interface TBasket {
    asap: boolean;
    basket_id: string;
    subtotal: number;
    total: number;
    total_service_selected?: number;
    total_service_unselected?: number;
    ccy: string;
    country: string;
    payment_intent: {
        client_secret: string;
    };
    errors: any[];
    basket: BasketItem[];
    auto_promo: string;
    ready_time: ReadyTime;

    delivery_fee?: number;
    service_fee: number;
    service_optional?: boolean;
    service_default?: boolean;
    promo_discount?: number;
    channel?: TChannel;
    codes: DiscountCode[];
    flow?: string;
    has_age_restricted_item: boolean;
}

export type BasketItem = Item & {
    collection_vat_type: string;
    delivery_vat_type: string;
    dine_in_vat_type: string;
    position: number;
    epos_id: string;
    epos_price_id: null;
    created_at: Date;
    updated_at: Date;
    deleted_at: null;
    modifier_ids: any[];
    quantity: number;
    selected_modifiers: any[];
    unselected_default_modifiers: any[];
    mods_total: number;
    item_total: number;
    offer_discount?: number; // what an offer takes off this line
    category: Category;
};

export interface Category {
    id: number;
    uid: string;
    menu_id: number;
    is_live: number;
    hidden_from: null;
    name: string;
    description: string;
    position: number;
    schedule: CategorySchedule;
    created_at: Date;
    updated_at: Date;
    deleted_at: null;
}

export interface CategorySchedule {
    mon_start_at?: Time;
    mon_end_at?: Time;
    tue_start_at?: Time;
    tue_end_at?: Time;
    wed_start_at?: Time;
    wed_end_at?: Time;
    thu_start_at?: Time;
    thu_end_at?: Time;
    fri_start_at?: Time;
    fri_end_at?: Time;
    sat_start_at?: Time;
    sat_end_at?: Time;
    sun_start_at?: Time;
    sun_end_at?: Time;
}

export interface ReadyTime {
    is_asap: boolean;
    id: string;
    name: string;
}

export interface Price {
    deliveryFee: number;
    serviceFee: number;
    subtotal: number;
    total: number;
}

export type Order = {
    uid: string;
    channel: TChannel;
    time: string | undefined;
    asap: boolean;
    basket: string; //OrderBasket[]
    subtotal: number;
    flow: string;
    special_notes: string | null | undefined;
    remove_promo: boolean | undefined;
    address: string | undefined | null;
    delivery_venue: string | undefined | null;
    // when the lead of a group order checks out
    group_token?: string;
    group_participant_token?: string;
};

export type OrderBasket = {
    item_id: string;
    quantity: number;
    modifiers: { modifier_id: string }[];
};

export type DiscountCode = {
    type: 'promo' | 'giftcard' | 'pos';
    amount: number;
    text: string;
    reference: string;
    offer?: Offer; // when the discount is an offer that applied by itself: what it is, which the reference (a code nobody entered) is not
};

export type BasketContextData = {
    basket: TBasket | null | undefined;
    setBasket: (basket: TBasket | null) => void;

    basketItems: Item[];
    setBasketItems: (items: Item[]) => void;

    validateBasket: (removePromo: boolean, navigateToCheckout: boolean) => void;
    validateTimeslot: () => void;
    validatePromoCode: (code: string, email: string | null) => Promise<any>;
    validateMemberId: (email: string, member: string) => Promise<any>;

    clearBasket: () => void;
    addItem: (item: Item, quantity: number, edit: boolean) => void;
    removeItem: (item: Item) => void;

    validatedSite: TSite | null | undefined;
    validatedBasket: TBasket | null | undefined;

    price: Price;
    setPrice: (price: Price) => void;

    timeslot: Timeslot | null;
    setTimeslot: (timeslot: Timeslot | null) => void;

    tableNumber: string | null;
    setTableNumber: (tableNumber: string | null) => void;

    customerEmail: string | null;
    setCustomerEmail: (email: string | null) => void;

    loading: boolean;
    setLoading: (state: boolean) => void;

    error: string;
    setError: (error: string) => void;

    promoError: string;
    setPromoError: (error: string) => void;

    specialNotes: string | null | undefined;
    setSpecialNotes: (specialNote: string | null | undefined) => void;
};

export const BasketContext = createContext<BasketContextData>({
    basket: null,
    setBasket: () => {},

    basketItems: [],
    setBasketItems: () => {},

    validateBasket: () => {},
    validateTimeslot: () => {},
    validatePromoCode: () => new Promise<any>((resolve, reject) => {}),
    validateMemberId: () => new Promise<any>((resolve, reject) => {}),
    clearBasket: () => {},
    addItem: () => {},
    removeItem: () => {},

    validatedSite: undefined,
    validatedBasket: undefined,

    price: {
        deliveryFee: 0,
        serviceFee: 0,
        subtotal: 0,
        total: 0,
    },
    setPrice: () => {},

    timeslot: null,
    setTimeslot: () => { },

    tableNumber: null,
    setTableNumber: () => {},

    customerEmail: null,
    setCustomerEmail: () => {},

    loading: false,
    setLoading: () => {},

    error: '',
    setError: () => {},

    promoError: '',
    setPromoError: () => {},

    specialNotes: null,
    setSpecialNotes: () => {}
});
